import { forwardRef } from 'react'
import type { ComponentPropsWithoutRef, ElementRef } from 'react'

import {
  Badge,
  Button,
  ChevronDownIcon,
  Flex,
  IconButton,
  Tag as PopcornTag,
  TagIcon,
  Text,
  useMediaQuery,
} from '@buffer-mono/popcorn'

import styles from './TagSelector.module.css'

export interface TagSelectorButtonTag {
  key: string
  name: string
  color: string
}

export const TagSelectorButton = forwardRef<
  ElementRef<typeof Button>,
  Omit<ComponentPropsWithoutRef<typeof Button>, 'children'> & {
    tags: readonly TagSelectorButtonTag[]
    maxVisibleTags?: number
  }
>(({ tags, maxVisibleTags = Infinity, ...props }, ref) => {
  const isSmallViewport = useMediaQuery('(width <= 800px)')

  const sortedTags = tags.toSorted((a, b) => a.name.localeCompare(b.name, 'en'))
  const visibleTags =
    maxVisibleTags < sortedTags.length
      ? sortedTags.slice(0, maxVisibleTags)
      : sortedTags
  const remainingTagsCount = sortedTags.length - visibleTags.length

  const label = tags.length === 0 ? 'Tags' : 'Add and edit tags'

  if (isSmallViewport && tags.length === 0) {
    return (
      <IconButton
        {...props}
        ref={ref}
        className={styles.tagsButton}
        variant="secondary"
        label={label}
        data-count={visibleTags.length}
      >
        <TagIcon />
      </IconButton>
    )
  }

  return (
    <Button
      {...props}
      ref={ref}
      className={styles.tagsButton}
      variant="secondary"
      aria-label={label}
      data-count={visibleTags.length}
    >
      {tags.length === 0 ? (
        <>
          <TagIcon />
          <span>Tags</span>
        </>
      ) : (
        <Flex as="span" gap="xs" align="center">
          {isSmallViewport ? (
            <>
              <TagIcon />
              <Badge size="small">{tags.length.toString()}</Badge>
            </>
          ) : (
            <>
              {visibleTags.map((tag) => (
                <Flex key={tag.key} gap="2xs" align="center">
                  <PopcornTag
                    className={styles.tagColorSquare}
                    color={tag.color}
                  >
                    {' '}
                  </PopcornTag>
                  <Text className={styles.tagName} weight="medium" truncate>
                    {tag.name}
                  </Text>
                </Flex>
              ))}
              {maxVisibleTags && remainingTagsCount > 0 && (
                <Text weight="medium">+{remainingTagsCount}</Text>
              )}
            </>
          )}
        </Flex>
      )}
      <ChevronDownIcon />
    </Button>
  )
})

TagSelectorButton.displayName = 'TagSelectorButton'
